<template>
  <div class="angular-iframe"
       :class="{'is-mobile' : $isMobile() || isMobileApp,
                  'is-desktop': !$isMobile() && !isMobileApp,
                    'isDisplayed' : isAngularDisplayed,
                    'isFullscreen' : isFullscreen,
                    'isModalMode': isAngularModalOpen}">
    <iframe id="angular-iframe"
            class="angular-iframe"
            :src="getAngularURL"
            v-show="isAngularDisplayed || isAngularModalOpen"
            data-qa="angular-iframe"
            :data-staff-uid="staffUid"
            :data-business-uid="businessUid"
            :data-mobile-layout="mobileLayoutAttr"
            title="angularjs"></iframe>
    <MainLoader v-if="!isAngularLoaded" data-qa="layoutLoader"></MainLoader>
  </div>
</template>

<script>

import eventListenerService from '@/utils/eventCommunicationUtils/eventListenerService';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import mobileAppService from '@/utils/appUtils/mobileAppService';
import { mapActions, mapGetters } from 'vuex';
import MainLoader from '@/modules/layout/mainLoader/MainLoader.vue';

export default {
  name: 'AngularDelegator',
  components: { MainLoader },
  data() {
    return {
      currentRoute: '',
    };
  },
  async created() {
    this.setCurrentRoute(window.location);

    eventListenerService.addListener('on-message', async (message) => {
      if (message.data?.doneLoading) {
        this.setIsAngularLoaded(true);
        this.syncSafeAreaInset();
        outgoingPostMessageUtils.post('init-complete', { initDone: true });
      }
      if (message.data?.authenticated) {
        mobileAppService.registerDevice();
      }
    });
  },
  mounted() {
    window.addEventListener('resize', this.syncSafeAreaInset);
  },
  methods: {
    ...mapActions('AngularStore', ['setIsAngularLoaded']),
    setCurrentRoute(url) {
      // This is a result of differences between local and integration envs
      if (url.pathname.includes('/app')) {
        this.currentRoute = url.pathname.substring(url.pathname.indexOf('/app') + 4);
      } else {
        this.currentRoute = url.pathname;
      }
      this.currentRoute += url.search;
      this.currentRoute += url.hash;
    },
    // The embedded Angular app runs in an iframe where env(safe-area-inset-*) is always 0.
    // Measure the inset here (top-level document) and expose it to the same-origin iframe.
    syncSafeAreaInset() {
      const angularRoot = this.angularDocumentRoot();
      if (!angularRoot) return;
      angularRoot.style.setProperty('--safe-area-inset-bottom', `${this.measureSafeAreaInsetBottom()}px`);
    },
    angularDocumentRoot() {
      try {
        return document.getElementById('angular-iframe')?.contentDocument?.documentElement ?? null;
      } catch (crossOriginInDev) {
        return null;
      }
    },
    measureSafeAreaInsetBottom() {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:fixed;bottom:0;left:0;width:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none;';
      document.body.appendChild(probe);
      const insetBottom = probe.getBoundingClientRect().height;
      probe.remove();
      return insetBottom;
    },
  },
  computed: {
    ...mapGetters('CommonStore', ['isMobileApp']),
    ...mapGetters('AngularStore', ['isAngularDisplayed', 'isFullscreen', 'isAngularLoaded']),
    ...mapGetters('ModalStore', ['isAngularModalOpen']),
    ...mapGetters('AuthStore', ['staffUid', 'businessUid']),
    // POV is the single source of truth for the mobile layout. It is exposed to
    // the embedded (same-origin) Angular app as a data attribute on the iframe,
    // so Angular reads it via `window.frameElement` and stops re-detecting from
    // the User-Agent — which misclassifies tablets as phones (SUPPORT-13084).
    // Angular then forwards it on to its nested vue iframe via `MobileView`.
    mobileLayoutAttr() {
      return this.$isMobile() ? 'true' : 'false';
    },
    getAngularURL() {
      const url = new URL(`${window.location.protocol}//${window.location.host}/app${this.currentRoute}`);
      url.searchParams.set('child_app', 'true');
      if (window.pov_config.NODE_ENV === 'development') {
        url.port = '7200';
      }
      return url.toString();
    },
  },
  destroyed() {
    window.removeEventListener('message', this.listener);
    window.removeEventListener('resize', this.syncSafeAreaInset);
  },
};

</script>
<style scoped lang="scss">
.angular-iframe {
  pointer-events: all;
  width: 100%;
  height: 100%;
  border: unset;
  display: block;
  position: absolute;
}

.is-mobile {
  background: transparent;
  position: absolute;
  top: 0;
  pointer-events: none;

  &.isDisplayed {
    pointer-events: all;
  }

  &.isFullscreen {
    z-index: 100;
  }
}

.is-desktop {
  z-index: 0;

  &.isDisplayed {
    z-index: 0;
  }

  &.isFullscreen {
    z-index: 100;
  }

  &.isModalMode {
    transition: z-index 500ms step-end;
  }
}

</style>
